import { isNil } from 'lodash';
import { Observable, map } from 'rxjs';
import { environment } from '@utility/app.util';

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import {
  IDomain,
  ICompliance,
  IDomainServer,
  IPageSettings,
  IComplianceServer,
  ISelfSignupPayload,
  IPageSettingsServer,
  ISelfSignupSettings,
  ISelfSignupSettingsServer,
  ISelfSignupSocialConnectSettings,
  ISelfSignupSocialConnectSettingsServer,
} from '@lib/self-signup.interface';
import { IUserGroup } from '@lib/users.interface';
import { UsergroupsInclusions } from '@admin/settings//util';
import { IAdminUser, IAdminUserServer } from '@lib/admin-users.interface';

@Injectable({
  providedIn: 'root',
})
export class SelfSignupService {
  constructor(private http: HttpClient) { }

  public getSelfSignupSettings(): Observable<ISelfSignupSettings> {
    return this.http
      .get<{ data: ISelfSignupSettingsServer }>(`${environment.apiUrl}/v2/client/signup-settings`)
      .pipe(map((response: { data: ISelfSignupSettingsServer }) => this.transformSelfSignupSettings(response.data)));
  }

  public getSignupSettings(): Observable<ISelfSignupSettings> {
    return this.http
      .get<{ data: ISelfSignupSettingsServer }>(`${environment.sociuuhubApiUrl}/signup/settings`)
      .pipe(map((response: { data: ISelfSignupSettingsServer }) => this.transformSelfSignupSettings(response.data)));
  }

  public saveSelfSignupSettings(payload: ISelfSignupPayload): Observable<ISelfSignupSettings> {
    return this.http
      .post<{ data: ISelfSignupSettingsServer }>(`${environment.apiUrl}/v2/client/signup-settings`, payload)
      .pipe(map((response: { data: ISelfSignupSettingsServer }) => this.transformSelfSignupSettings(response.data)));
  }

  private transformSelfSignupSettings(response: ISelfSignupSettingsServer): ISelfSignupSettings {
    if (!response) return null;
    return ({
      id: response.id,
      createdAt: response.created_at,
      signupURL: response.signup_url,
      defaultDepartment: isNil(response.default_department) ? undefined : {
        ...response.default_department,
        title: response.default_department.name,
      },
      hideDeptSelection: !isNil(response.default_department) ? !!response.hide_department : false,
      allowSignup: !!response.allow_signup,
      smsVerification: !!response.sms_verification,
      sendWelcomeEmail: !!response.send_welcome_email,
      emailVerification: !!response.email_verification,
      sendThankyouEmail: !!response.send_thankyou_email,
      adminApprovalRequired: !!response.admin_approval_required,
      allowUsergroupsSelection: !!response?.allow_usergroups_selection,
      countrySelectionRequired: !!response?.country_selection_required,
      adminIDs: this.transformSelfSignupSettingsAdmins(response?.admins),
      filterUsergroupsByCountry: !!response?.filter_usergroups_by_country,
      clientCountry: {
        id: response.client_country?.code,
        code: response.client_country?.code,
        name: response.client_country?.name,
        title: response.client_country?.name,
        createdAt: response.client_country?.created_at,
        dialingCode: response.client_country?.dialing_code,
      },
      usergroups: response.usergroups.map((usergroup: IUserGroup): IUserGroup => ({
        ...usergroup,
        isDeletionDisabled: true,
      })),
      pageSettings: this.transformSelfSignupSettingsPageSettings(response.page_settings),
      allowedDomains: this.transformSelfSignupSettingsAllowedDomains(response.allowed_domains),
      signupCompliances: this.transformSelfSignupSettingsCompliances(response.signup_compliances),
      emailDigestAdminIDs: this.transformSelfSignupSettingsAdmins(response.email_digest_admin_ids),

      approverCustomSelection: response.approver_custom_selection,
      approverAdmins: isNil(response.approvers) ? [] : [...response.approvers.map(approver => ({ ...approver, title: approver.name }))],

      usergroupRestriction: isNil(response.usergroup_restriction) ? UsergroupsInclusions.SHOW_ALL : response.usergroup_restriction,
      usergroupRestrictionIds: isNil(response.usergroup_restriction_selection) ? [] : [...response.usergroup_restriction_selection],

      // IP Access Control
      customMessage: response.ip_blocked_message,
      ipAccessControlMode: response.ip_access_control_mode,
      ipAccessControlEnabled: !!response.ip_access_control_enabled,
      ipAddresses: response.ip_rules?.map((rule: { id: number, ip: string }): string => rule.ip) || [],

      socialConnectSettings: this.transformSelfSignupSettingsSocialConnect(response.social_connect_settings),
    });
  }

  private transformSelfSignupSettingsAdmins(response: IAdminUserServer[]): IAdminUser[] {
    return response?.map((admin: IAdminUserServer): IAdminUser => ({
      id: admin.id,
      title: admin.name,
    })) || [];
  }

  private transformSelfSignupSettingsAllowedDomains(response: IDomainServer[]): IDomain[] {
    if (!response) return [];
    return response.map((domain: IDomainServer): IDomain => ({
      domainName: domain.domain_name,
      includeSubdomains: domain.include_subdomains,
    }));
  }

  private transformSelfSignupSettingsPageSettings(response: IPageSettingsServer): IPageSettings {
    return {
      language: response?.language,
      teaserText: response?.teaser_text,
      confirmationText: response.confirmation_text,
      parsedTeaserText: response?.parsed_teaser_text,
    };
  }

  private transformSelfSignupSettingsCompliances(response: IComplianceServer[]): ICompliance[] {
    return response.map((item: IComplianceServer): ICompliance => ({
      id: item.compliance_id,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      approvalRequired: !!item.approval_required,
      compliance: {
        title: item.compliance.title,
        contents: item.compliance.contents,
        createdAt: item.compliance.created_at,
        updatedAt: item.compliance.updated_at,
      },
    }));
  }

  private transformSelfSignupSettingsSocialConnect(response: ISelfSignupSocialConnectSettingsServer): ISelfSignupSocialConnectSettings {
    if (!response) return null;
    return {
      linkedinMode: response.signup_sc_mode,
      reminderEnabled: response.signup_sc_reminder_enabled,
      reminderSubject: response.signup_sc_reminder_subject,
      reminderBody: response.signup_sc_reminder_body,
      reminderIntervalDays: response.signup_sc_reminder_interval_days,
    };
  }
}
